<template>
    <TransitionSlide :direction="transitionDirection">
        <div class="login-content" :class="classForCurrentState" :key="authenticationStep">
            <InfoText
                :title="modalTitle"
                :perex="modalPerex"
                :showBackLink="authenticationStep == 'LoginPass'"
                @prevStep="prevStepInternal"
                class="login-content__header"
            />

            <LoginWelcome
                ref="loginWelcome"
                v-show="authenticationStep == 'LoginWelcome'"
                class="login-content__welcome"
            />

            <PhoneInput
                ref="phoneInput"
                v-show="authenticationStep == 'LoginPhone'"
                class="login-content__phone"
                name="phone"
                @input-key-up="inputKeyUpEventHandler"
                v-model="authenticationData.userName"
                v-model:error-message="authenticationErrors.phoneErrorMessage"
            />

            <PasswordInput
                ref="passwordInput"
                v-show="authenticationStep == 'LoginPass'"
                class="login-content__password"
                @input-key-up="inputKeyUpEventHandler"
                v-model="authenticationData.password"
                name="password"
                v-model:error-message="authenticationErrors.passwordErrorMessage"
                :focus="true"
            />

            <PinInput
                ref="pinInput"
                v-show="authenticationStep == 'ForgPinLog'"
                class="login-content__pin"
                v-model="authenticationData.pin"
                name="sms_code"
                :phone-number="authenticationData.userName"
                v-model:error-message="authenticationErrors.pinErrorMessage"
                @resend-pin="resendPin"
            />

            <NewPasswordInput
                ref="newPasswordInput"
                v-show="authenticationStep == 'ForgNewPass'"
                class="login-content__new-password"
                v-model="authenticationData.password"
                name="new-password"
                :passwordErrorMessage="newPasswordErrorMessage"
                v-model:error-message="authenticationErrors.newPasswordErrorMessage"
            />

            <LoginFooter
                class="login-content__footer"
                :authentication-step="authenticationStep"
                @nextStep="nextStepInternal"
                @goToLoginPass="goToLoginPassInternal"
                @login="loginInternal"
            />
        </div>
    </TransitionSlide>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import InfoText from "@/account/components/shared/info-text.vue";
import LoginWelcome from "@/account/components/login/login-welcome.vue";
import PhoneInput from "@/account/components/inputs/phone-input.vue";
import PasswordInput from "@/account/components/inputs/password-input.vue";
import PinInput from "@/account/components/inputs/pin-input.vue";
import NewPasswordInput from "@/account/components/inputs/new-password-input.vue";
import TransitionSlide from "@sazka/design-system/src/transitions/transition-slide.vue";
import { TransitionSlideDirection } from "@sazka/design-system/src/transitions/transition-slide-direction";
import LoginFooter from "./login-footer.vue";
import useUserAccount from "@/account/use-user-account";
import useAuthentication from "../../composables/use-authentication";
import useAuthenticationData, {
    authenticationErrors,
    authenticationData,
} from "../../composables/use-authentication-data";
import useAuthenticationFlows, { authenticationStep } from "@/account/composables/use-authentication-flows";

const { loginUser } = useUserAccount();
const { sendPinProcess, nextAuthenticationStep, prevAuthenticationStep } = useAuthentication();
const { clearAuthenticationErrors } = useAuthenticationData();
const { openLoginPassModal } = useAuthenticationFlows();

const loginWelcome = ref(null);
const phoneInput = ref(null);
const passwordInput = ref(null);
const pinInput = ref(null);
const newPasswordInput = ref(null);
const newPasswordErrorMessage = ref<string>();
const transitionDirection = ref<TransitionSlideDirection>("rtl");

const nextStepInternal = () => {
    clearAuthenticationErrors();

    if (
        (authenticationStep.value == "LoginPhone" && !phoneInput.value.checkIfIsValid()) ||
        (authenticationStep.value == "ForgPinLog" && !pinInput.value.checkIfIsValid()) ||
        (authenticationStep.value == "ForgNewPass" && !newPasswordInput.value.checkIfIsValid())
    ) {
        return;
    }

    nextAuthenticationStep();
};

const prevStepInternal = () => {
    prevAuthenticationStep();
};
const goToLoginPassInternal = () => {
    openLoginPassModal();
};

const inputKeyUpEventHandler = async (event: KeyboardEvent) => {
    if (event.key === "Enter") {
        event.preventDefault();
        if (authenticationStep.value == "LoginWelcome" || authenticationStep.value == "LoginPass") {
            await loginUser();
        } else {
            nextStepInternal();
        }
    }
};

const resendPin = async () => {
    authenticationErrors.pinErrorMessage = "";
    await sendPinProcess(true);
    pinInput.value.pinResended(!authenticationErrors.pinErrorMessage);
};

const loginInternal = async () => {
    if (
        (authenticationStep.value == "LoginWelcome" && !loginWelcome.value.checkIfIsValid()) ||
        (authenticationStep.value == "LoginPass" && !passwordInput.value.checkIfIsValid())
    ) {
        return;
    }

    await loginUser();
    console.log();
};

const modalTitle = computed(() => {
    switch (authenticationStep.value) {
        case "LoginWelcome":
            return "Vítejte v Sazka Klubu!";
        case "LoginPhone":
            return "Přihlášení";
        case "LoginPass":
            return "Zadejte heslo";
        case "ForgPinGen":
        case "ForgPinLog":
            return "Zapomenuté heslo";
        case "ForgPinOk":
            return "Ověření bylo úspěšné. V&nbsp;dalším kroku si nastavte nové heslo.";
        case "ForgNewPass":
            return "Vytvořit nové heslo";
        case "ForgNewPassOk":
            return "Hotovo";
        default:
            return "";
    }
});

const modalPerex = computed(() => {
    switch (authenticationStep.value) {
        case "LoginWelcome":
            return "Nyní se můžete přihlásit a využívat výhody Sazka Klubu naplno.";
        case "LoginPhone":
        case "LoginPass":
            return "do vašeho účtu Sazka Klubu";
        case "ForgPinGen":
            return "Pro nastavení nového hesla vám na telefon zašleme 4místný PIN kód.";
        case "ForgNewPassOk":
            return "Heslo bylo úspěšně změněno, nyní se můžete přihlásit.";
        case "RegNewPass":
        case "RegVerPhone":
        case "RegPinLog":
            return "Už jsme skoro tam!";
        default:
            return "";
    }
});

const classForCurrentState = computed(() => {
    switch (authenticationStep.value) {
        case "LoginWelcome":
            return "login-content--welcome";
        case "ForgPinGen":
        case "ForgPinLog":
        case "ForgPinOk":
        case "ForgNewPass":
        case "ForgNewPassOk":
            return "login-content--forg-pass";
        default:
            return "";
    }
});

watch(authenticationStep, (newAccountState, oldAccountState) => {
    if (
        (oldAccountState == "LoginPass" && newAccountState == "LoginPhone") ||
        (oldAccountState == "LoginPass" && newAccountState == "LoginWelcome") ||
        (oldAccountState == "ForgPinGen" && newAccountState == "LoginPass") ||
        (oldAccountState == "ForgPinLog" && newAccountState == "LoginPass")
    ) {
        transitionDirection.value = "ltr";
    } else {
        transitionDirection.value = "rtl";
    }
});
</script>
